import { useMemo } from 'react';
import { useAppSelector } from '@/_store/hooks';
import type {
  DeliverymanInfos,
  DeliverymanNumbers,
  PlaceNumbers,
  PlacesInfos,
} from '../types';
import { ENUM_DELIVERY_MODEL_LABELS } from '../constants';

interface UseDashboardAggregatedDataProps {
  deliverymenInfo?: DeliverymanInfos;
  placesInfos?: PlacesInfos;
}

export const useDashboardAggregateData = ({
  deliverymenInfo,
  placesInfos,
}: UseDashboardAggregatedDataProps) => {
  const selectedsDeliveryModel = useAppSelector(
    state => state.deliveryModel.selectedsDeliveryModel,
  );
  const defaultDeliverymanNumbers: DeliverymanNumbers = {
    total: 0,
    online: 0,
    available: 0,
    inDelivery: 0,
    offline: 0,
  };

  const defaultPlaceNumbers: PlaceNumbers = {
    total: 0,
    closed: 0,
    open: 0,
    totalPlacesWithInProgressDeliveries: 0,
    totalPlacesWithPendingDeliveries: 0,
    totalInProgressOrders: 0,
    totalPlacesWithInProgressOrders: 0,
  };

  const getDeliverymanData = (): DeliverymanNumbers => {
    if (
      selectedsDeliveryModel.includes(
        ENUM_DELIVERY_MODEL_LABELS.OWN_AND_CLOUD_DELIVERYMAN,
      )
    ) {
      return (
        deliverymenInfo?.ownAndCloudDeliveryman || defaultDeliverymanNumbers
      );
    }
    if (
      selectedsDeliveryModel.includes(
        ENUM_DELIVERY_MODEL_LABELS.CLOUD_DELIVERYMAN,
      )
    ) {
      return deliverymenInfo?.cloudDeliveryman || defaultDeliverymanNumbers;
    }
    if (
      selectedsDeliveryModel.includes(
        ENUM_DELIVERY_MODEL_LABELS.OWN_DELIVERYMAN,
      )
    ) {
      return deliverymenInfo?.ownDeliveryman || defaultDeliverymanNumbers;
    }
    return deliverymenInfo || defaultDeliverymanNumbers;
  };

  const getPlaceData = (): PlaceNumbers => {
    if (
      selectedsDeliveryModel.includes(
        ENUM_DELIVERY_MODEL_LABELS.OWN_AND_CLOUD_PLACE,
      )
    ) {
      return placesInfos?.ownAndCloudPlaces || defaultPlaceNumbers;
    }
    if (
      selectedsDeliveryModel.includes(ENUM_DELIVERY_MODEL_LABELS.CLOUD_PLACE)
    ) {
      return placesInfos?.cloudPlaces || defaultPlaceNumbers;
    }
    if (selectedsDeliveryModel.includes(ENUM_DELIVERY_MODEL_LABELS.OWN_PLACE)) {
      return placesInfos?.ownPlaces || defaultPlaceNumbers;
    }
    return placesInfos || defaultPlaceNumbers;
  };

  const deliverymanData = useMemo(() => {
    return getDeliverymanData();
  }, [deliverymenInfo, selectedsDeliveryModel]);

  const placeData = useMemo(() => {
    return getPlaceData();
  }, [placesInfos, selectedsDeliveryModel]);

  return {
    deliverymanData,
    placeData,
  };
};
